import {
    Box,
    Checkbox,
    IconButton,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import { Check, X } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';
import RoiColor from '~/components/Structures/RoiColor';
import type { DicomColor } from '~/domain/dicom/dicomColor';
import type { RoiPlanEntry, RoiSource } from './roiPropagationPlan';

interface RoiPlanTableProps {
    entries: readonly RoiPlanEntry[];
    colors: ReadonlyMap<string, DicomColor>;
    onIncludedChange: (name: string, included: boolean) => void;
    onSourceChange: (
        name: string,
        source: RoiSource,
        confirmedModelRoiName?: string
    ) => void;
    disabled?: boolean;
}

const ROW_HEIGHT_PX = 26;
// Lines the Contour+ name up under the structure name.
const PAIRING_INDENT_PX = 30;

export default function RoiPlanTable({
    entries,
    colors,
    onIncludedChange,
    onSourceChange,
    disabled,
}: RoiPlanTableProps) {
    const intl = useIntl();
    const includedCount = entries.filter((entry) => entry.included).length;
    const allIncluded = includedCount === entries.length;

    if (entries.length === 0) {
        return null;
    }

    return (
        <Box display="flex" flexDirection="column" minHeight={0} height="100%">
            <Typography
                fontSize={11}
                color="text.secondary"
                sx={{
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    pt: 0.75,
                }}
            >
                <FormattedMessage id="replanning.rois.heading" />
            </Typography>
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                pb={0.25}
            >
                <Box display="flex" alignItems="center" gap={0.5}>
                    <Checkbox
                        size="small"
                        checked={allIncluded}
                        indeterminate={includedCount > 0 && !allIncluded}
                        disabled={disabled}
                        onChange={(event) =>
                            entries.forEach((entry) =>
                                onIncludedChange(
                                    entry.name,
                                    event.target.checked
                                )
                            )
                        }
                        inputProps={{
                            'aria-label': intl.formatMessage({
                                id: 'replanning.rois.includeAll',
                            }),
                        }}
                        data-testid="replanning-roi-include-all"
                        sx={{ p: 0.25 }}
                    />
                    <Typography fontSize={11} color="text.secondary">
                        <FormattedMessage
                            id="replanning.rois.count"
                            values={{
                                included: includedCount,
                                total: entries.length,
                            }}
                        />
                    </Typography>
                </Box>
            </Box>

            <Box flex={1} minHeight={0} overflow="auto">
                {entries.map((entry) => {
                    const color = colors.get(entry.name);
                    const choose = (source: RoiSource) =>
                        // A suggestion holds only for the model structure shown when it was taken.
                        onSourceChange(
                            entry.name,
                            source,
                            source === 'contour' && entry.needsConfirmation
                                ? entry.modelRoiName
                                : undefined
                        );
                    return (
                        <Box
                            key={entry.name}
                            sx={{ opacity: entry.included ? 1 : 0.45 }}
                            mb={entry.needsConfirmation ? 0.75 : 0}
                            data-testid={`replanning-roi-row-${entry.name}`}
                        >
                            <Box
                                display="flex"
                                alignItems="center"
                                gap={0.5}
                                minHeight={ROW_HEIGHT_PX}
                            >
                                <Checkbox
                                    size="small"
                                    checked={entry.included}
                                    disabled={disabled}
                                    onChange={(event) =>
                                        onIncludedChange(
                                            entry.name,
                                            event.target.checked
                                        )
                                    }
                                    inputProps={{ 'aria-label': entry.name }}
                                    sx={{ p: 0.25 }}
                                />
                                {color && <RoiColor color={color} size={9} />}
                                <Typography
                                    fontSize={11}
                                    flex={1}
                                    minWidth={0}
                                    noWrap
                                    title={entry.name}
                                    color={
                                        entry.included
                                            ? 'text.primary'
                                            : 'text.disabled'
                                    }
                                >
                                    {entry.name}
                                </Typography>
                                <ToggleButtonGroup
                                    exclusive
                                    size="small"
                                    value={entry.source}
                                    disabled={disabled || !entry.included}
                                    onChange={(_, value: RoiSource | null) =>
                                        value && choose(value)
                                    }
                                    sx={{
                                        '& .MuiToggleButton-root': {
                                            px: 0.75,
                                            py: 0.125,
                                            fontSize: 9.5,
                                            lineHeight: 1.4,
                                            textTransform: 'none',
                                        },
                                    }}
                                >
                                    <ToggleButton
                                        value="propagate"
                                        data-testid={`replanning-roi-propagate-${entry.name}`}
                                    >
                                        <FormattedMessage id="replanning.rois.propagate" />
                                    </ToggleButton>
                                    <Tooltip
                                        title={
                                            entry.contourable ? (
                                                ''
                                            ) : (
                                                <FormattedMessage id="replanning.rois.notContourable" />
                                            )
                                        }
                                    >
                                        <Box component="span" display="flex">
                                            <ToggleButton
                                                value="contour"
                                                disabled={
                                                    disabled ||
                                                    !entry.included ||
                                                    !entry.contourable
                                                }
                                                data-testid={`replanning-roi-contour-${entry.name}`}
                                            >
                                                <FormattedMessage id="replanning.rois.contour" />
                                            </ToggleButton>
                                        </Box>
                                    </Tooltip>
                                </ToggleButtonGroup>
                            </Box>
                            {entry.needsConfirmation && (
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={0.25}
                                    pl={`${PAIRING_INDENT_PX}px`}
                                    data-testid={`replanning-roi-pairing-${entry.name}`}
                                >
                                    <Typography
                                        fontSize={10}
                                        noWrap
                                        minWidth={0}
                                        color="text.secondary"
                                        title={entry.modelRoiName}
                                    >
                                        <FormattedMessage
                                            id="replanning.rois.contourName"
                                            values={{
                                                modelName: entry.modelRoiName,
                                            }}
                                        />
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        disabled={disabled || !entry.included}
                                        color={
                                            entry.source === 'contour'
                                                ? 'primary'
                                                : 'default'
                                        }
                                        aria-pressed={
                                            entry.source === 'contour'
                                        }
                                        aria-label={intl.formatMessage(
                                            {
                                                id: 'replanning.rois.useContour',
                                            },
                                            {
                                                modelName: entry.modelRoiName,
                                            }
                                        )}
                                        onClick={() => choose('contour')}
                                        data-testid={`replanning-roi-accept-${entry.name}`}
                                        sx={{ p: 0.25 }}
                                    >
                                        <Check size={12} />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        disabled={disabled || !entry.included}
                                        color={
                                            entry.source === 'propagate'
                                                ? 'primary'
                                                : 'default'
                                        }
                                        aria-pressed={
                                            entry.source === 'propagate'
                                        }
                                        aria-label={intl.formatMessage({
                                            id: 'replanning.rois.keepPropagated',
                                        })}
                                        onClick={() => choose('propagate')}
                                        data-testid={`replanning-roi-reject-${entry.name}`}
                                        sx={{ p: 0.25 }}
                                    >
                                        <X size={12} />
                                    </IconButton>
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}
